import {
  DEFAULT_ORG_FILTER_MODE,
  effectiveOrgFilterMode,
  isOrgFavorited,
  isOrgFilterOffered,
  isOrgInMyOrgs as isOrgInMyOrgsShared,
  matchesOrgFilterMode as matchesOrgFilterModeShared,
  type OrgMenuFilterMode,
} from '@pypestream/design-system';
import { ShortOrgType } from './permissions-table.types';

export type { OrgMenuFilterMode };
export { DEFAULT_ORG_FILTER_MODE, isOrgFavorited };

type FilterableOrg = Pick<ShortOrgType, 'id' | 'isPrimaryAccount'>;

/**
 * The shared predicates (design-system `smart-components/utils/org-favorites.ts`,
 * PSTUDIO-3330 Unit 1 — also exported publicly from `@pypestream/design-system`
 * so this app does not keep a second copy of the "is this org in My Orgs" rule)
 * compare a candidate org's id against a single `primaryOrgId` — the shape a
 * TREE naturally has one of. Manager's `ShortOrgType` is a FLAT array where
 * each org already carries its OWN `isPrimaryAccount` boolean, so there is
 * nothing to look up: an org is its own primary-org candidate, or it is not.
 */
function primaryOrgIdFor(org: FilterableOrg): string | undefined {
  return org.isPrimaryAccount ? org.id : undefined;
}

/**
 * Whether `org` counts as being "in My Orgs" — either explicitly favorited,
 * or the primary/top org, which is always in My Orgs and never
 * user-togglable.
 */
export function isOrgInMyOrgs(
  org: FilterableOrg,
  favoriteOrgIds: string[] | undefined
): boolean {
  return isOrgInMyOrgsShared(org, favoriteOrgIds, primaryOrgIdFor(org));
}

/** Whether `org` should be shown under the given filter mode. */
export function matchesOrgFilterMode(
  org: FilterableOrg,
  mode: OrgMenuFilterMode,
  favoriteOrgIds: string[] | undefined
): boolean {
  return matchesOrgFilterModeShared(org, {
    mode,
    favoriteOrgIds,
    primaryOrgId: primaryOrgIdFor(org),
  });
}

/** The orgs matching the given filter mode, client-side (flat, no tree). */
export function filterOrgsBySegment(
  orgs: ShortOrgType[],
  mode: OrgMenuFilterMode,
  favoriteOrgIds: string[] | undefined
): ShortOrgType[] {
  return orgs.filter((org) => matchesOrgFilterMode(org, mode, favoriteOrgIds));
}

/** Number of orgs matching the given filter mode — feeds the segment counts. */
export function countOrgsMatchingMode(
  orgs: ShortOrgType[],
  mode: OrgMenuFilterMode,
  favoriteOrgIds: string[] | undefined
): number {
  return orgs.reduce(
    (total, org) =>
      total + (matchesOrgFilterMode(org, mode, favoriteOrgIds) ? 1 : 0),
    0
  );
}

/**
 * Org-name search for the roles table: trimmed, case-insensitive substring of
 * `org.name`. A blank term returns the SAME array, so a caller that memoizes on
 * the list does not re-render for an empty search box. An org without a name
 * never matches a non-empty term.
 *
 * Limitation: no diacritics normalization — "Zoe" does not match "Zoë".
 */
export function filterOrgsByName(
  orgs: ShortOrgType[],
  term: string
): ShortOrgType[] {
  const needle = term.trim().toLowerCase();
  if (!needle) return orgs;

  return orgs.filter((org) => org.name?.toLowerCase().includes(needle));
}

/**
 * Whether the roles table's search box is offered, and the term that applies.
 * Same threshold as the My / All / Hidden control (`isOrgFilterOffered`), applied
 * to the list on screen (`orgCount` = the orgs the chosen mode shows): with one
 * org there is nothing to look through. A term typed while there were
 * several must not outlive the field that could clear it, so it stops applying
 * while the field is hidden. It is kept, not erased: when the field comes back
 * it shows the term again and the term applies again.
 */
export function resolveRolesTableSearch({
  orgCount,
  term,
}: {
  orgCount: number;
  term: string;
}): { showSearch: boolean; appliedTerm: string } {
  const showSearch = isOrgFilterOffered(orgCount);
  return { showSearch, appliedTerm: showSearch ? term : '' };
}

/**
 * Which placeholder the search box carries: "Search all N Orgs" when the list on
 * screen is every org of the account, "Search N Orgs" (N = the list's own size)
 * when it is a part of it — the field searches the list on screen, so "all 33"
 * over a two-org "My Orgs" list would be a false claim. Same rule as the org
 * menu's.
 */
export function resolveRolesTableSearchPlaceholder({
  listedCount,
  totalCount,
}: {
  listedCount: number;
  totalCount: number;
}): { key: 'searchPlaceholder' | 'searchPlaceholderFiltered'; count: number } {
  return listedCount === totalCount
    ? { key: 'searchPlaceholder', count: totalCount }
    : { key: 'searchPlaceholderFiltered', count: listedCount };
}

/**
 * What the roles table shows for a given filter choice: whether the My / All /
 * Hidden control is offered, the rows to render, and whether the favorite stars
 * are drawn.
 *
 * Only the user's OWN profile is filterable (it is the only one whose stars are
 * editable). With a single org there is nothing to switch between and nothing
 * that can be starred, so the control goes — and the stored mode stops applying
 * at the same moment, because a mode nobody can see or change must never be
 * what empties the table (a lone org that is not flagged primary would vanish
 * under "My Orgs").
 *
 * `showStars` is the value the page hands to the table as `canEditFavorites`,
 * and `org-cell.tsx` wraps the whole star block in `{canEditFavorites && …}`, so
 * `false` also hides the read-only star of the home org. It is false for
 * another user's profile and in the "My Orgs" view, judged on the EFFECTIVE
 * mode so a lone org under a stored "my" still shows its star.
 */
export function resolveRolesTableOrgs({
  orgs,
  mode,
  favoriteOrgIds,
  isCurrentUser,
}: {
  orgs: ShortOrgType[];
  mode: OrgMenuFilterMode;
  favoriteOrgIds: string[] | undefined;
  isCurrentUser: boolean;
}): { showFilter: boolean; visibleOrgs: ShortOrgType[]; showStars: boolean } {
  if (!isCurrentUser) {
    return { showFilter: false, visibleOrgs: orgs, showStars: false };
  }

  const effectiveMode = effectiveOrgFilterMode(mode, orgs.length);

  return {
    showFilter: isOrgFilterOffered(orgs.length),
    visibleOrgs: filterOrgsBySegment(orgs, effectiveMode, favoriteOrgIds),
    showStars: effectiveMode !== 'my',
  };
}

export type OrgStarState = {
  icon: 'star' | 'star-filled';
  readOnly: boolean;
};

/**
 * Pure: which star icon an org row shows and whether it is clickable.
 * The primary/top org is always favorited and never user-togglable — it
 * reads filled and read-only regardless of `canEditFavorites`. Every other
 * org reflects `favoriteOrgIds` and is clickable only when the viewer may
 * edit favorites (their own profile — see `user-details.tsx`).
 *
 * Deliberately kept in this design-system/React-free module rather than in
 * `org-cell.tsx` itself: that file pulls several design-system Lit-backed
 * React wrappers (`TableCell`, `Stack`, `Icon`, …), and importing it from a
 * vitest file fails at module load ("Denied ID …global-styles.scss?inline")
 * — confirmed by actually trying it, not assumed. Keeping this predicate
 * here is what makes it unit-testable at all.
 */
export function resolveOrgStarState(
  org: FilterableOrg,
  favoriteOrgIds: string[],
  canEditFavorites: boolean
): OrgStarState {
  if (org.isPrimaryAccount) {
    return { icon: 'star-filled', readOnly: true };
  }

  return {
    icon: isOrgFavorited(org.id, favoriteOrgIds) ? 'star-filled' : 'star',
    readOnly: !canEditFavorites,
  };
}
